import { useState } from 'react'
import { router } from '@inertiajs/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { CheckCircle2, Clock, Pill, FlaskConical, User, Edit2, Phone, MapPin, Users, FileTextIcon, CreditCard, Home, AlertCircle, Activity, Calendar } from 'lucide-react'
import { PatientLayout } from '@/layouts/patient-layout'

interface PatientProfile {
  id: number
  display_name: string
  legal_name: string
  alias_name: string | null
  date_of_birth: string | null
  phone_number: string
  zip_code: string
  emergency_contacts: Array<{
    name: string | null
    phone: string | null
  }>
  consents: {
    sms: boolean
    data_share: boolean
    non_emergency: boolean
    hipaa_ack: boolean
    signed_at: string | null
  }
}

interface ExtendedDemographics {
  home_address: string | null
  allergies: string | null
  sex_assigned_at_birth: string | null
}

interface InsurancePrescription {
  plan_name: string | null
  id_number: string | null
  bin: string | null
  group_number: string | null
  pcn: string | null
}

interface ActiveMedication {
  id: number
  medication_name: string
  notes: string | null
  created_at: string
}

interface CareJourney {
  current_status: {
    status: string
    status_label: string
    effective_at: string
  } | null
  timeline: Array<{
    status: string
    status_label: string
    effective_at: string
    note: string | null
  }>
  days_in_current_stage: number
  case_opened_at: string | null
}

interface Screening {
  completed_at: string
  risk_score_level: string
}

interface LabResult {
  test_type: string
  test_type_label: string
  status: string
  ordered_at: string
  result_summary: string | null
  result_received_at: string | null
  is_screening: boolean
  is_baseline: boolean
  is_svr12: boolean
}

interface ProviderIntake {
  completion_percentage: number
  checklist: {
    cmp: boolean
    cbc: boolean
    hep_b: boolean
    fibrosis: boolean
    hiv: boolean
    genotype: boolean
  }
}

interface Treatment {
  regimen_name: string
  duration_weeks: number
  treatment_duration_label?: string
  treatment_directions?: string
  start_date: string
  end_date: string
  status_label: string
  upcoming_followups: Array<{
    follow_up_type: string
    type_label: string
    due_on: string
  }>
}

interface TreatmentPlan {
  instructions: string
  updated_at: string
}

interface CureStatus {
  is_cured: boolean
  cured_at: string | null
  confirmation_message: string | null
}

interface Appointment {
  id: number
  appointment_type: string
  type_label: string
  scheduled_at: string
  formatted_date: string
  formatted_time: string
  formatted_datetime: string
  duration_minutes: number
  location: string | null
  reason: string | null
  status: string
  status_label: string
  upcoming: boolean
  past: boolean
}

interface AppointmentsSummary {
  upcoming: Appointment[]
  past: Appointment[]
}

interface PatientProgressProps {
  patient_profile: PatientProfile
  extended_demographics: ExtendedDemographics
  insurance_prescription: InsurancePrescription
  active_medications: ActiveMedication[]
  care_journey: CareJourney
  screening: Screening | null
  labs: LabResult[]
  provider_intake: ProviderIntake | null
  treatment: Treatment | null
  treatment_plan: TreatmentPlan | null
  appointments: AppointmentsSummary
  cure_status: CureStatus
}

const contactInfoSchema = z.object({
  phone_number: z.string().min(1, 'Phone number is required'),
  zip_code: z.string().min(5, 'ZIP code must be at least 5 characters'),
  emergency_contact_1_name: z.string().optional(),
  emergency_contact_1_phone: z.string().optional(),
  emergency_contact_2_name: z.string().optional(),
  emergency_contact_2_phone: z.string().optional(),
  emergency_contact_3_name: z.string().optional(),
  emergency_contact_3_phone: z.string().optional(),
})

type ContactInfoFormData = z.infer<typeof contactInfoSchema>

export default function Index(props: PatientProgressProps) {
  const { patient_profile, extended_demographics, insurance_prescription, active_medications, care_journey, screening, labs, provider_intake, treatment, treatment_plan, appointments, cure_status } = props
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<ContactInfoFormData>({
    resolver: zodResolver(contactInfoSchema),
    defaultValues: {
      phone_number: patient_profile.phone_number || '',
      zip_code: patient_profile.zip_code || '',
      emergency_contact_1_name: patient_profile.emergency_contacts[0]?.name || '',
      emergency_contact_1_phone: patient_profile.emergency_contacts[0]?.phone || '',
      emergency_contact_2_name: patient_profile.emergency_contacts[1]?.name || '',
      emergency_contact_2_phone: patient_profile.emergency_contacts[1]?.phone || '',
      emergency_contact_3_name: patient_profile.emergency_contacts[2]?.name || '',
      emergency_contact_3_phone: patient_profile.emergency_contacts[2]?.phone || '',
    },
  })

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getRiskBadgeColor = (risk: string) => {
    switch (risk?.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200'
      case 'moderate': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low': return 'bg-green-100 text-green-800 border-green-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const onSubmitContactInfo = async (data: ContactInfoFormData) => {
    setIsSubmitting(true)

    try {
      await fetch('/patients/progress', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('patient_session_token')}`,
        },
        body: JSON.stringify({ patient: data }),
      })

      // Reload the page to show updated data
      router.reload()
      setIsEditDialogOpen(false)
    } catch (error) {
      console.error('Failed to update contact information:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Care journey funnel stages
  const careStages = [
    { status: 'intake', label: 'Intake', icon: User },
    { status: 'awaiting_testing', label: 'Testing', icon: FlaskConical },
    { status: 'awaiting_provider_intake', label: 'Provider Review', icon: User },
    { status: 'ready_for_tx', label: 'Ready for Treatment', icon: CheckCircle2 },
    { status: 'on_tx', label: 'On Treatment', icon: Pill },
    { status: 'svr12_pending', label: 'SVR12 Pending', icon: Clock },
    { status: 'cured', label: 'Cured', icon: CheckCircle2 },
  ]

  const currentStageIndex = care_journey.current_status
    ? careStages.findIndex(stage => stage.status === care_journey.current_status?.status)
    : -1

  return (
    <div className="flex flex-1 flex-col">
      {/* Page Header */}
      <header className="flex h-16 shrink-0 items-center gap-2 border-b px-6">
        <div className="flex flex-1 items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">My Progress</h1>
            <p className="text-sm text-muted-foreground">
              Welcome back, {patient_profile.display_name}
            </p>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-auto bg-background">
        <div className="mx-auto max-w-7xl px-6 py-8">
          <div className="space-y-6">
            {/* Profile Card - Now at the top */}
            <Card className="border-2">
              <CardHeader className="bg-muted/50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5 text-muted-foreground" />
                      My Profile
                    </CardTitle>
                    <CardDescription>Your contact information and health summary</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditDialogOpen(true)}
                    className="gap-2"
                  >
                    <Edit2 className="h-4 w-4" />
                    Edit
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Legal Name</p>
                      <p className="text-base font-medium text-foreground">{patient_profile.legal_name}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3.5 w-3.5" />
                          Phone
                        </p>
                        <p className="text-base text-foreground">{patient_profile.phone_number || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          ZIP Code
                        </p>
                        <p className="text-base text-foreground">{patient_profile.zip_code || 'Not provided'}</p>
                      </div>
                    </div>
                    {patient_profile.emergency_contacts.some(c => c.name) && (
                      <div className="border-t border-border pt-4">
                        <p className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-1">
                          <Users className="h-3.5 w-3.5" />
                          Emergency Contacts
                        </p>
                        <div className="space-y-2">
                          {patient_profile.emergency_contacts.filter(c => c.name).map((contact, index) => (
                            <div key={index} className="flex items-center gap-2 rounded-md bg-muted/50 p-2">
                              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-foreground">
                                {index + 1}
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-foreground">{contact.name}</span>
                                <span className="text-muted-foreground"> • {contact.phone}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Extended Demographics - Read Only */}
                    {(extended_demographics.home_address || extended_demographics.allergies || extended_demographics.sex_assigned_at_birth) && (
                      <div className="border-t border-border pt-4">
                        <p className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-1">
                          <Home className="h-3.5 w-3.5" />
                          Additional Information
                        </p>
                        <div className="space-y-3">
                          {extended_demographics.home_address && (
                            <div className="rounded-md bg-muted/50 p-3">
                              <p className="text-xs font-medium text-muted-foreground mb-1">Home Address</p>
                              <p className="text-sm text-foreground whitespace-pre-wrap">{extended_demographics.home_address}</p>
                            </div>
                          )}
                          {extended_demographics.allergies && (
                            <div className="rounded-md bg-muted/50 p-3">
                              <p className="text-xs font-medium text-muted-foreground mb-1">Allergies</p>
                              <p className="text-sm text-foreground whitespace-pre-wrap">{extended_demographics.allergies}</p>
                            </div>
                          )}
                          {extended_demographics.sex_assigned_at_birth && (
                            <div className="rounded-md bg-muted/50 p-3">
                              <p className="text-xs font-medium text-muted-foreground mb-1">Sex Assigned at Birth</p>
                              <p className="text-sm text-foreground">{extended_demographics.sex_assigned_at_birth}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Health Summary - Risk Screening, Labs, Treatment */}
                  <div className="border-t border-border pt-6">
                    <h3 className="text-base font-semibold mb-4">Health Summary</h3>
                    <div className="grid gap-4 md:grid-cols-3">
                      {/* Risk Screening */}
                      {screening && (
                        <div className="rounded-lg border border-border p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="h-8 w-8 rounded-full bg-red-500/20 dark:bg-red-500/30 flex items-center justify-center">
                              <FlaskConical className="h-4 w-4 text-red-600 dark:text-red-400" />
                            </div>
                            <p className="text-sm font-semibold">Risk Screening</p>
                          </div>
                          <Badge className={`${getRiskBadgeColor(screening.risk_score_level)} text-xs font-semibold px-3 py-1 mb-2`}>
                            {screening.risk_score_level.toUpperCase()} RISK
                          </Badge>
                          <p className="text-xs text-muted-foreground">Completed {formatDate(screening.completed_at)}</p>
                        </div>
                      )}

                      {/* Lab Tests */}
                      {labs && labs.length > 0 && (
                        <div className="rounded-lg border border-border p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="h-8 w-8 rounded-full bg-blue-500/20 dark:bg-blue-500/30 flex items-center justify-center">
                              <FlaskConical className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <p className="text-sm font-semibold">Lab Tests</p>
                          </div>
                          <p className="text-2xl font-bold text-foreground mb-1">{labs.length}</p>
                          <p className="text-xs text-muted-foreground">Tests completed</p>
                        </div>
                      )}

                      {/* Treatment */}
                      {treatment && (
                        <div className="rounded-lg border border-border p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="h-8 w-8 rounded-full bg-purple-500/20 dark:bg-purple-500/30 flex items-center justify-center">
                              <Pill className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                            </div>
                            <p className="text-sm font-semibold">Treatment</p>
                          </div>
                          <p className="text-2xl font-bold text-foreground mb-1">{treatment.duration_weeks}</p>
                          <p className="text-xs text-muted-foreground">Weeks duration</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cure Status Banner */}
            {cure_status.is_cured && (
              <Card className="border-2 border-green-500/30 bg-green-500/10">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-green-500/20 ring-4 ring-green-500/10">
                    <CheckCircle2 className="h-7 w-7 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-green-900 dark:text-green-100">Congratulations! You're Cured!</h3>
                    <p className="mt-1 text-sm text-green-800 dark:text-green-200">{cure_status.confirmation_message}</p>
                  </div>
                </div>
              </CardContent>
              </Card>
            )}

          {/* Care Journey Progress Bar */}
          {care_journey.current_status && (
            <Card className="border-2 border-border">
              <CardHeader className="bg-muted/50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl">My Care Journey</CardTitle>
                    <CardDescription>Your progress through the HCV treatment program</CardDescription>
                  </div>
                  <Badge className="bg-indigo-500 dark:bg-indigo-600 text-white text-sm px-4 py-2">
                    {care_journey.current_status.status_label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-6 pb-8">
                {/* Progress Bar */}
                <div className="relative">
                  <div className="flex items-center justify-between mb-8">
                    {careStages.map((stage, index) => {
                      const Icon = stage.icon
                      const isActive = index === currentStageIndex
                      const isCompleted = index < currentStageIndex
                      const isFuture = index > currentStageIndex

                      return (
                        <div key={stage.status} className="flex flex-col items-center" style={{ flex: 1 }}>
                          {/* Icon Circle */}
                          <div
                            className={`relative z-10 flex h-12 w-12 items-center justify-center rounded-full border-4 transition-all ${
                              isActive
                                ? 'border-background bg-indigo-500 dark:bg-indigo-600 text-white shadow-lg ring-4 ring-indigo-500/20'
                                : isCompleted
                                ? 'border-background bg-green-500 dark:bg-green-600 text-white shadow-md'
                                : 'border-background bg-muted text-muted-foreground'
                            }`}
                          >
                            {isCompleted ? (
                              <CheckCircle2 className="h-6 w-6" />
                            ) : (
                              <Icon className="h-5 w-5" />
                            )}
                          </div>

                          {/* Label */}
                          <div className="mt-3 text-center">
                            <p
                              className={`text-xs font-medium ${
                                isActive ? 'text-indigo-900 dark:text-indigo-300' : isCompleted ? 'text-green-900 dark:text-green-300' : 'text-muted-foreground'
                              }`}
                            >
                              {stage.label}
                            </p>
                            {isActive && (
                              <p className="mt-1 text-xs text-indigo-600 dark:text-indigo-400">Current</p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Progress Line */}
                  <div
                    className="absolute top-6 left-0 right-0 h-1 bg-muted"
                    style={{
                      left: `calc(${100 / careStages.length / 2}%)`,
                      right: `calc(${100 / careStages.length / 2}%)`,
                    }}
                  >
                    <div
                      className="h-full bg-gradient-to-r from-green-500 to-indigo-500 transition-all duration-500"
                      style={{
                        width: `${(currentStageIndex / (careStages.length - 1)) * 100}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Last Updated and Time in Stage */}
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div className="text-center rounded-lg bg-muted/50 p-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Time in Current Stage</p>
                    <p className={`text-2xl font-bold ${
                      care_journey.days_in_current_stage <= 3
                        ? 'text-green-600 dark:text-green-400'
                        : care_journey.days_in_current_stage <= 7
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-orange-600 dark:text-orange-400'
                    }`}>
                      {care_journey.days_in_current_stage} {care_journey.days_in_current_stage === 1 ? 'day' : 'days'}
                    </p>
                  </div>
                  <div className="text-center rounded-lg bg-muted/50 p-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Case Opened</p>
                    <p className="text-sm font-medium text-foreground">
                      {care_journey.case_opened_at ? formatDate(care_journey.case_opened_at) : 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {/* Upcoming Appointments Card */}
          {treatment?.upcoming_followups && treatment.upcoming_followups.length > 0 && (
            <Card className="border-2 border-border">
              <CardHeader className="bg-muted/50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Clock className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                      Upcoming Appointments
                    </CardTitle>
                    <CardDescription>Your scheduled follow-up visits</CardDescription>
                  </div>
                  <Badge className="bg-orange-500 dark:bg-orange-600 text-white">
                    {treatment.upcoming_followups.length} {treatment.upcoming_followups.length === 1 ? 'Appointment' : 'Appointments'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid gap-4 md:grid-cols-2">
                  {treatment.upcoming_followups.map((followup, index) => (
                    <div
                      key={index}
                      className="group relative overflow-hidden rounded-lg border-2 border-border bg-card p-4 transition-all hover:shadow-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500/20 dark:bg-orange-500/30">
                              <Clock className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                            </div>
                            <Badge variant="outline" className="text-xs">
                              Appointment {index + 1}
                            </Badge>
                          </div>
                          <h4 className="font-semibold text-foreground mb-1">
                            {followup.type_label}
                          </h4>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="font-medium">{formatDate(followup.due_on)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 rounded-md bg-orange-500/10 border border-orange-500/20 p-2">
                        <p className="text-xs text-orange-900 dark:text-orange-200">
                          <span className="font-medium">Note:</span> Please arrive 15 minutes early
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                {treatment.upcoming_followups.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-sm">No upcoming appointments scheduled</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Two Column Layout */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Lab Results */}
              {labs && labs.length > 0 && (
                <Card className="border-2">
                  <CardHeader className="bg-muted/50">
                    <CardTitle className="flex items-center gap-2">
                      <FlaskConical className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Lab Results
                    </CardTitle>
                    <CardDescription>Your recent HCV test results</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {labs.map((lab, index) => (
                        <div key={index} className="flex items-start justify-between border-b border-border pb-4 last:border-0 last:pb-0">
                          <div className="space-y-1">
                            <p className="font-medium text-foreground">{lab.test_type_label}</p>
                            {lab.result_summary && (
                              <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">{lab.result_summary}</p>
                            )}
                            <div className="flex flex-col gap-0.5">
                              <p className="text-xs text-muted-foreground">Ordered: {formatDate(lab.ordered_at)}</p>
                              {lab.result_received_at && (
                                <p className="text-xs text-muted-foreground">Received: {formatDate(lab.result_received_at)}</p>
                              )}
                            </div>
                          </div>
                          <Badge variant="outline" className="capitalize shrink-0">
                            {lab.status.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Insurance & Prescription Card */}
              {(insurance_prescription.plan_name) && (
                <Card className="border-2">
                  <CardHeader className="bg-muted/50">
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-green-600 dark:text-green-400" />
                      My Insurance & Prescription
                    </CardTitle>
                    <CardDescription>Your insurance coverage details</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="rounded-lg bg-green-500/10 border border-green-500/20 p-4">
                        <p className="text-sm font-medium text-green-900 dark:text-green-200 mb-3">Plan Information</p>
                        <div className="grid grid-cols-2 gap-3">
                          {insurance_prescription.plan_name && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground">Health Plan</p>
                              <p className="text-sm text-foreground font-medium">{insurance_prescription.plan_name}</p>
                            </div>
                          )}
                          {insurance_prescription.id_number && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground">ID Number</p>
                              <p className="text-sm text-foreground">{insurance_prescription.id_number}</p>
                            </div>
                          )}
                          {insurance_prescription.bin && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground">BIN</p>
                              <p className="text-sm text-foreground">{insurance_prescription.bin}</p>
                            </div>
                          )}
                          {insurance_prescription.group_number && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground">Group Number</p>
                              <p className="text-sm text-foreground">{insurance_prescription.group_number}</p>
                            </div>
                          )}
                          {insurance_prescription.pcn && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground">PCN</p>
                              <p className="text-sm text-foreground">{insurance_prescription.pcn}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Active Medications Card */}
              {active_medications && active_medications.length > 0 && (
                <Card className="border-2">
                  <CardHeader className="bg-muted/50">
                    <CardTitle className="flex items-center gap-2">
                      <Pill className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      My Current Medications
                    </CardTitle>
                    <CardDescription>{active_medications.length} active medications</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      {active_medications.map((med) => (
                        <div key={med.id} className="rounded-lg border border-border bg-muted/50 p-4">
                          <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-purple-500/20 dark:bg-purple-500/30">
                              <Pill className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-foreground">{med.medication_name}</p>
                              {med.notes && (
                                <p className="text-sm text-muted-foreground mt-1">{med.notes}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Provider Intake Status */}
              {provider_intake && (
                <Card className="border-2">
                  <CardHeader className="bg-muted/50">
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      Provider Review Status
                    </CardTitle>
                    <CardDescription>Your care team is reviewing baseline test results</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="rounded-lg bg-indigo-500/10 border border-indigo-500/20 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-medium text-indigo-900 dark:text-indigo-200">Baseline Labs Review</p>
                          <Badge className="bg-indigo-500/20 text-indigo-900 dark:text-indigo-200">
                            {provider_intake.completion_percentage}% Complete
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(provider_intake.checklist).map(([key, value]) => (
                            <div key={key} className={`p-2 border rounded text-sm text-center ${
                              value ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 text-green-900 dark:text-green-200' : 'bg-muted border-border text-muted-foreground'
                            }`}>
                              {value ? '✓ ' : '○ '}{key.toUpperCase()}
                            </div>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Your provider will review these results before starting treatment
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Treatment Information */}
              {treatment && (
                <Card className="border-2">
                  <CardHeader className="bg-muted/50">
                    <CardTitle className="flex items-center gap-2">
                      <Pill className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      My Treatment
                    </CardTitle>
                    <CardDescription>Your current HCV treatment plan</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="rounded-lg bg-purple-500/10 p-4">
                        <p className="text-sm font-medium text-purple-900 dark:text-purple-200">Medication</p>
                        <p className="mt-1 text-base font-semibold text-purple-700 dark:text-purple-300">{treatment.regimen_name}</p>
                      </div>
                      {treatment.treatment_directions && (
                        <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/20 p-4">
                          <p className="text-sm font-medium text-purple-900 dark:text-purple-200">How to Take</p>
                          <p className="mt-1 text-sm text-purple-800 dark:text-purple-300">{treatment.treatment_directions}</p>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Duration</p>
                          <p className="text-lg font-semibold text-foreground">
                            {treatment.treatment_duration_label || `${treatment.duration_weeks} weeks`}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Status</p>
                          <Badge className="mt-1 bg-purple-500/20 text-purple-900 dark:text-purple-200">{treatment.status_label}</Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Start Date</p>
                          <p className="text-sm text-foreground">{formatDate(treatment.start_date)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Expected End</p>
                          <p className="text-sm text-foreground">{formatDate(treatment.end_date)}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Treatment Plan Instructions */}
              {treatment_plan && (
                <Card className="border-2">
                  <CardHeader className="bg-muted/50">
                    <CardTitle className="flex items-center gap-2">
                      <FileTextIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Treatment Instructions
                    </CardTitle>
                    <CardDescription>Important guidelines from your care team</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="rounded-lg bg-blue-500/10 p-4">
                      <div className="whitespace-pre-wrap text-sm text-foreground leading-relaxed">
                        {treatment_plan.instructions}
                      </div>
                      <div className="mt-4 text-xs text-muted-foreground">
                        Last updated: {formatDate(treatment_plan.updated_at)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Appointments */}
              {(appointments.upcoming.length > 0 || appointments.past.length > 0) && (
                <Card className="border-2">
                  <CardHeader className="bg-muted/50">
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      My Appointments
                    </CardTitle>
                    <CardDescription>Your scheduled and past appointments</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-6">
                      {/* Upcoming Appointments */}
                      {appointments.upcoming.length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold text-foreground mb-3">Upcoming</h3>
                          <div className="space-y-3">
                            {appointments.upcoming.map((appointment) => (
                              <div
                                key={appointment.id}
                                className="rounded-lg border-2 border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/20 p-4"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Badge variant="outline" className="bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700">
                                        {appointment.type_label}
                                      </Badge>
                                      <Badge variant="outline" className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700">
                                        {appointment.status_label}
                                      </Badge>
                                    </div>
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2 text-sm">
                                        <Clock className="h-4 w-4 text-muted-foreground" />
                                        <span className="font-medium text-foreground">{appointment.formatted_datetime}</span>
                                        <span className="text-muted-foreground">({appointment.duration_minutes} min)</span>
                                      </div>
                                      {appointment.location && (
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                          <MapPin className="h-4 w-4" />
                                          <span>{appointment.location}</span>
                                        </div>
                                      )}
                                      {appointment.reason && (
                                        <div className="text-sm text-muted-foreground mt-2">
                                          {appointment.reason}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Past Appointments */}
                      {appointments.past.length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold text-foreground mb-3">Past Appointments</h3>
                          <div className="space-y-2">
                            {appointments.past.map((appointment) => (
                              <div
                                key={appointment.id}
                                className="rounded-lg border border-border bg-muted/30 p-3"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-sm font-medium text-foreground">{appointment.type_label}</span>
                                      <Badge
                                        variant="outline"
                                        className={
                                          appointment.status === 'completed'
                                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700'
                                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700'
                                        }
                                      >
                                        {appointment.status_label}
                                      </Badge>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                      <Clock className="h-3 w-3" />
                                      <span>{appointment.formatted_date} at {appointment.formatted_time}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Edit Contact Info Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Contact Information</DialogTitle>
            <DialogDescription>
              Update your phone number, ZIP code, and trusted emergency contacts.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={form.handleSubmit(onSubmitContactInfo)} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="phone_number">Phone Number *</Label>
                <Input
                  id="phone_number"
                  placeholder="+1234567890"
                  {...form.register('phone_number')}
                />
                {form.formState.errors.phone_number && (
                  <p className="text-sm text-red-600">{form.formState.errors.phone_number.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip_code">ZIP Code *</Label>
                <Input
                  id="zip_code"
                  placeholder="94102"
                  {...form.register('zip_code')}
                />
                {form.formState.errors.zip_code && (
                  <p className="text-sm text-red-600">{form.formState.errors.zip_code.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <h4 className="text-sm font-semibold text-gray-900">Trusted Emergency Contacts</h4>

              {/* Contact 1 */}
              <div className="grid gap-4 md:grid-cols-2 rounded-lg border p-4">
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_1_name">Contact 1 Name</Label>
                  <Input
                    id="emergency_contact_1_name"
                    placeholder="John Doe"
                    {...form.register('emergency_contact_1_name')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_1_phone">Contact 1 Phone</Label>
                  <Input
                    id="emergency_contact_1_phone"
                    placeholder="+1234567890"
                    {...form.register('emergency_contact_1_phone')}
                  />
                </div>
              </div>

              {/* Contact 2 */}
              <div className="grid gap-4 md:grid-cols-2 rounded-lg border p-4">
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_2_name">Contact 2 Name</Label>
                  <Input
                    id="emergency_contact_2_name"
                    placeholder="Jane Smith"
                    {...form.register('emergency_contact_2_name')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_2_phone">Contact 2 Phone</Label>
                  <Input
                    id="emergency_contact_2_phone"
                    placeholder="+1234567890"
                    {...form.register('emergency_contact_2_phone')}
                  />
                </div>
              </div>

              {/* Contact 3 */}
              <div className="grid gap-4 md:grid-cols-2 rounded-lg border p-4">
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_3_name">Contact 3 Name</Label>
                  <Input
                    id="emergency_contact_3_name"
                    placeholder="Bob Johnson"
                    {...form.register('emergency_contact_3_name')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_3_phone">Contact 3 Phone</Label>
                  <Input
                    id="emergency_contact_3_phone"
                    placeholder="+1234567890"
                    {...form.register('emergency_contact_3_phone')}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  )
}



// Use the patient-specific layout with sidebar
Index.layout = (page: React.ReactNode) => <PatientLayout>{page}</PatientLayout>
